/**
 * Cobrança — backend no geocoder (FastAPI), exposto em /geo/v1/billing.
 * Mensalidade por embarcação (device) + gateway Asaas. Auth via cookie de sessão.
 */

export type InvoiceStatus = 'pendente' | 'pago' | 'vencido' | 'cancelado';

export interface BillingConfig {
  tenant_id: number;
  valor_por_device: number;
  dia_vencimento: number;
  ativo: boolean;
  asaas_customer_id: string | null;
}

export interface InvoiceItem {
  device_uniqueid: string | null;
  device_name: string | null;
  valor: number;
}

export interface Invoice {
  id: number;
  tenant_id: number;
  tenant_nome: string | null;
  competencia: string;
  valor: number;
  qtd_devices: number;
  vencimento: string | null;
  status: InvoiceStatus;
  gateway: string | null;
  asaas_id: string | null;
  asaas_invoice_url: string | null;
  asaas_bankslip_url: string | null;
  pix_payload: string | null;
  pago_em: string | null;
  created_at: string | null;
  itens?: InvoiceItem[] | null;
}

export interface GenerateResult {
  competencia: string;
  criadas: number;
  puladas: number;
  total: number;
  detalhes: Array<Record<string, unknown>>;
}

export type GatewayProvider = 'asaas' | 'mercadopago' | 'outro' | 'manual';

export interface GatewayConfig {
  tenant_id: number;
  provider: GatewayProvider; // ativo
  asaas_api_key_mask: string | null;
  asaas_webhook_set: boolean;
  mp_access_token_mask: string | null;
  mp_webhook_set: boolean;
  sandbox: boolean;
  enabled: boolean;
}

export interface GatewayConfigInput {
  provider: GatewayProvider; // ativo
  // em cada segredo: undefined/null = mantém o atual; '' = limpa
  asaas_api_key?: string | null;
  asaas_webhook_token?: string | null;
  mp_access_token?: string | null;
  mp_webhook_token?: string | null;
  sandbox: boolean;
  enabled: boolean;
}

export const GATEWAY_PROVIDERS: { value: GatewayProvider; label: string }[] = [
  { value: 'asaas', label: 'Asaas' },
  { value: 'mercadopago', label: 'Mercado Pago' },
  { value: 'outro', label: 'Outro (manual)' },
];

const BASE = '/geo/v1/billing';

async function req<T>(url: string, init?: RequestInit): Promise<T> {
  const r = await fetch(url, {
    credentials: 'include',
    headers: init?.body ? { 'Content-Type': 'application/json' } : undefined,
    ...init,
  });
  if (!r.ok) {
    let detail = `${r.status}`;
    try {
      const j = await r.json();
      if (j?.detail) detail = j.detail;
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  if (r.status === 204) return undefined as T;
  return r.json() as Promise<T>;
}

export const STATUS_LABEL: Record<InvoiceStatus, string> = {
  pendente: 'Pendente',
  pago: 'Pago',
  vencido: 'Vencido',
  cancelado: 'Cancelado',
};

export const billingApi = {
  clientes: () => req<Array<{ id: number; nome: string }>>(`${BASE}/clientes`),
  getGateway: (tenantId = 0) => req<GatewayConfig>(`${BASE}/gateway?tenant_id=${tenantId}`),
  setGateway: (tenantId: number, body: GatewayConfigInput) =>
    req<GatewayConfig>(`${BASE}/gateway?tenant_id=${tenantId}`, { method: 'PUT', body: JSON.stringify(body) }),
  getConfig: (tenantId: number) => req<BillingConfig>(`${BASE}/config?tenant_id=${tenantId}`),
  setConfig: (tenantId: number, body: { valor_por_device: number; dia_vencimento: number; ativo: boolean }) =>
    req<BillingConfig>(`${BASE}/config?tenant_id=${tenantId}`, { method: 'PUT', body: JSON.stringify(body) }),
  generate: (body: { competencia: string; tenant_id?: number | null; emitir_asaas?: boolean }) =>
    req<GenerateResult>(`${BASE}/invoices/generate`, { method: 'POST', body: JSON.stringify(body) }),
  listInvoices: (params?: { competencia?: string; status?: string; tenant_id?: number }) => {
    const p = new URLSearchParams();
    if (params?.competencia) p.set('competencia', params.competencia);
    if (params?.status) p.set('status', params.status);
    if (params?.tenant_id !== undefined) p.set('tenant_id', String(params.tenant_id));
    const qs = p.toString();
    return req<Invoice[]>(`${BASE}/invoices${qs ? `?${qs}` : ''}`);
  },
  myInvoices: () => req<Invoice[]>(`${BASE}/my-invoices`),
  getInvoice: (id: number) => req<Invoice>(`${BASE}/invoices/${id}`),
  pay: (id: number) => req<Invoice>(`${BASE}/invoices/${id}/pay`, { method: 'POST' }),
  cancel: (id: number) => req<Invoice>(`${BASE}/invoices/${id}/cancel`, { method: 'POST' }),
  processOverdue: () => req<{ ok: boolean; vencidas: number; inadimplentes: number }>(`${BASE}/process-overdue`, { method: 'POST' }),
};
